import { useEffect, useRef } from "react";
import io, { Socket } from "socket.io-client";
import type { MergeFailureReason } from "../../components/MergeItem/mergeItem.types";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";

export const ITEM_MERGE_COMPLETED_EVENT = "item_merge_completed";

/**
 * A "Merge with an existing item" run has finished. Published by simb-integration-ms's item-merge
 * worker, after it writes the run's status, and relayed by simb-notification-ms into the
 * `user:<id>` room of the user who started it.
 */
export interface ItemMergeCompletedEvent {
  userId: string;
  accountId?: string;
  requestId: string;
  currentItemId: string;
  currentBoardId?: string;
  state: "done" | "partial" | "failed";
  reason?: MergeFailureReason;
  failedColumns?: Array<{ title: string; reason: string; boardName?: string }>;
  otherItemId?: string;
  otherBoardId?: string;
  message: string;
}

interface UseItemMergeStreamArgs {
  userId: string | number | null | undefined;
  accountId?: string | number | null;
  onCompleted: (event: ItemMergeCompletedEvent) => void;
  // Room emits are not buffered: an outcome fired while the socket was down is lost, so the
  // consumer re-checks anything still pending here.
  onReconnect?: () => void;
}

/**
 * Subscribes to the notification socket and forwards each merge outcome addressed to this user.
 * Used by the merge flow (matched on requestId) and by the item view (matched on the item), so
 * the item view refreshes even when Settings was closed mid-merge.
 */
const useItemMergeStream = ({ userId, accountId, onCompleted, onReconnect }: UseItemMergeStreamArgs) => {
  // Keep latest callbacks in refs so the socket effect does not reconnect on every render.
  const onCompletedRef = useRef(onCompleted);
  const onReconnectRef = useRef(onReconnect);
  onCompletedRef.current = onCompleted;
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    if (!userId) return;

    const socket: Socket = io(`${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId ?? ""}`);

    socket.on(ITEM_MERGE_COMPLETED_EVENT, (data: ItemMergeCompletedEvent) => {
      // The server only emits to this user's room; this is defense in depth.
      if (String(data?.userId) === String(userId)) onCompletedRef.current(data);
    });

    socket.io.on("reconnect", () => {
      onReconnectRef.current?.();
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, accountId]);
};

export default useItemMergeStream;
